import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "./ui/button";
import {
  Menu, X, BookOpen, GraduationCap, Terminal, Brain, ArrowRight, MessageSquare, Code, Flame
} from "lucide-react";
import { getBootcampStatus } from "../lib/bootcamp";

const NAV_LINKS = [
  { path: "/tutorials",   label: "Tutorials",      icon: BookOpen,      badge: null                              },
  { path: "/bootcamp",    label: "Bootcamp",        icon: GraduationCap, badge: null                              },
  { path: "/problems",    label: "Problems",        icon: Code,          badge: null                              },
  { path: "/playground",  label: "Code Simulator",  icon: Terminal,      badge: null                              },
  { path: "/ai-interview",label: "AI Interview",    icon: Brain,         badge: { text: "Soon", color: "amber" } },
  { path: "/contact",     label: "Contact",         icon: MessageSquare, badge: null                              },
];

const LogoIcon = () => (
  <div className="w-8 h-8 bg-primary flex items-center justify-center flex-shrink-0">
    <Terminal className="w-5 h-5 text-[#0A0F1E]" />
  </div>
);

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  const [bootcampStatus, setBootcampStatus] = useState(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll, { passive: true });
    
    // Fetch bootcamp status
    getBootcampStatus().then(data => setBootcampStatus(data));

    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => { document.body.style.overflow = "unset"; };
  }, [isOpen]);

  useEffect(() => { setIsOpen(false); }, [location.pathname]);

  const isActive = (path) =>
    path === "/" ? location.pathname === "/" : location.pathname.startsWith(path);

  return (
    <>
      <nav
        data-testid="navbar"
        className={`fixed top-0 left-0 right-0 z-[60] transition-all duration-300 ${
          scrolled || isOpen ? 'bg-[#0A0F1E] border-b border-[#1F2937]' : 'bg-transparent border-b border-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-14">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2.5 group flex-shrink-0 z-[70]">
              <LogoIcon />
              <span className="font-bold text-[15px] text-white tracking-tight group-hover:text-primary transition-colors">
                DevHire<span className="text-primary">Lab</span>
              </span>
            </Link>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-1 absolute left-1/2 -translate-x-1/2">
              {NAV_LINKS.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`relative flex items-center gap-1.5 px-3 py-1.5 rounded-none text-[12px] font-bold transition-all duration-200 ${
                    isActive(link.path)
                      ? "text-primary bg-primary/5"
                      : "text-muted-foreground hover:text-white hover:bg-white/5"
                  }`}
                >
                  {isActive(link.path) && (
                    <motion.div
                      layoutId="nav-active"
                      className="absolute -bottom-[1px] left-0 right-0 h-[1px] bg-primary"
                    />
                  )}
                  <link.icon className="w-3.5 h-3.5 flex-shrink-0" />
                  {link.label}
                  {link.path === '/bootcamp' && bootcampStatus && (
                    <div 
                      className="w-1.5 h-1.5 rounded-full animate-pulse" 
                      style={{ 
                        backgroundColor: bootcampStatus.status === 'enrolling' ? '#00E676' : 
                                        bootcampStatus.status === 'in_progress' ? '#3B82F6' :
                                        bootcampStatus.status === 'completed' ? '#6B7280' : '#F59E0B'
                      }} 
                    />
                  )}
                  {link.badge && (
                    <span className={`px-1.5 py-0.5 rounded-none text-[8px] font-semibold uppercase ${link.badge.color === 'amber' ? 'bg-accent text-[#0A0F1E]' : 'bg-primary text-[#0A0F1E]'}`}>
                      {link.badge.text}
                    </span>
                  )}
                </Link>
              ))}
            </div>

            {/* Desktop CTA */}
            <div className="hidden md:flex items-center gap-3">
              <Link to="/bootcamp">
                <Button size="sm" variant="outline" className="border-primary text-primary hover:bg-primary hover:text-[#0A0F1E]">
                  Join Bootcamp
                </Button>
              </Link>
            </div>

            {/* Mobile Toggle */}
            <button
              className="md:hidden z-[70] p-2 -mr-2 rounded-none text-slate-400 hover:text-white transition-colors"
              onClick={() => setIsOpen(!isOpen)}
              aria-label="Toggle menu"
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </nav>

      {/* Full-screen Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, x: "100%" }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: "100%" }}
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
            className="bg-[#0A0F1E] fixed inset-0 z-[55] md:hidden flex flex-col p-6 pt-24"
          >
            <div className="flex flex-col gap-2 relative z-10">
              {NAV_LINKS.map((link, i) => (
                <motion.div
                  key={link.path}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.1 }}
                >
                  <Link
                    to={link.path}
                    className={`flex items-center justify-between p-4 rounded-none border transition-all ${
                      isActive(link.path)
                        ? "bg-primary/10 border-primary/20 text-primary"
                        : "bg-white/5 border-white/5 text-muted-foreground"
                    }`}
                  >
                    <div className="flex items-center gap-4 text-sm font-semibold uppercase tracking-tight">
                      <link.icon className="w-5 h-5" />
                      {link.label}
                    </div>
                    <ArrowRight className="w-4 h-4 opacity-40" />
                  </Link>
                </motion.div>
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="mt-auto flex flex-col gap-4 pb-8"
            >
              <Link to="/bootcamp" className="w-full">
                <Button className="w-full h-14 rounded-none bg-primary text-[#0A0F1E] font-semibold text-base">
                  JOIN BOOTCAMP
                </Button>
              </Link>
              <p className="text-center text-[10px] text-muted-foreground uppercase tracking-[0.3em]">
                Your Python Journey Starts Here
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
